import { HiMicrophone, HiStop } from "react-icons/hi";
import { Button, Input } from "antd";
import { useMemo, useRef, useState } from "react";
import { useMantineTheme } from "@mantine/core";
import { RecordingObjectType } from "../../enums";
import { useMicRecorder } from "../../hooks/use-mic-recorder";
import { ProcessingCircle } from "../ProcessingCircle";
import styles from "../Modus/Chat.module.css";

export interface ITypeOrRecordProps {
  disabled?: boolean;
  recordingObjectType: RecordingObjectType;
  onProcess: (transcript: string, objectId: string) => Promise<void>;
  hideRecordButton?: boolean;
  autoFocus?: boolean;
  defaultText?: string;
  submitButtonText?: string;
  clearTextOnSubmit?: boolean;
  onlyType?: boolean;
  placeHolder?: string;
  rows?: number;
}

enum InputMode {
  TYPING = "typing",
  RECORDING = "recording"
}

export const TypeOrRecord = ({
  disabled = false,
  recordingObjectType,
  onProcess,
  hideRecordButton,
  autoFocus,
  defaultText = "",
  submitButtonText = "Send",
  clearTextOnSubmit = true,
  onlyType = false,
  placeHolder = "Type to talk to Vo (press Shift + Enter for a new line)",
  rows = 2
}: ITypeOrRecordProps) => {
  const [inputValue, setInputValue] = useState<string>(defaultText || "");
  const theme = useMantineTheme();

  const {
    startRecording,
    stopRecording,
    isRecording,
    isProcessingAudio,
    isBusy
  } = useMicRecorder();

  const inputRef = useRef<any>(null);

  const inputMode = useMemo(() => {
    if (!!onlyType) {
      return InputMode.TYPING;
    }

    if (isBusy) {
      return InputMode.RECORDING;
    } else if (inputValue) {
      return InputMode.TYPING;
    } else {
      return InputMode.RECORDING;
    }
  }, [inputValue, isBusy]);

  const onSubmit = () => {
    onProcess(inputValue, null);

    if (clearTextOnSubmit) {
      setInputValue("");
    }
  };

  const handleInputComponent = useMemo(() => {
    if (!isProcessingAudio) {
      return (
        <Button
          onClick={(e) => {
            e.stopPropagation();
            onSubmit();
          }}
          disabled={disabled || inputValue === ""}
        >
          {submitButtonText}
        </Button>
      );
    } else {
      if (isProcessingAudio) {
        return <ProcessingCircle text="" color={theme.colors.darkBlue[0]} />;
      } else if (isRecording && !hideRecordButton) {
        return (
          <Button disabled={disabled} onClick={stopRecording}>
            <HiStop />
          </Button>
        );
      } else if (!hideRecordButton) {
        return (
          <Button
            disabled={disabled}
            onClick={() => {
              startRecording(recordingObjectType, onProcess);
            }}
          >
            <HiMicrophone />
          </Button>
        );
      } else {
        return null;
      }
    }
  }, [inputMode, isProcessingAudio, isRecording, hideRecordButton, inputValue]);

  return (
    <div
      style={{
        width: "100%",
        display: "flex",
        alignItems: "center"
      }}
    >
      <Input.TextArea
        ref={inputRef}
        autoFocus={autoFocus}
        className={styles.customTextArea}
        style={{
          marginRight: handleInputComponent ? "8px" : "0"
        }}
        defaultValue={defaultText}
        placeholder={
          placeHolder ||
          "Type to talk to Vo (press Shift + Enter for a new line)"
        }
        value={inputValue}
        onChange={(e) => {
          e.stopPropagation();
          setInputValue(e.target.value);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            if (e.shiftKey) {
              // Allow shift+enter to create a new line
              return;
            } else if (!isBusy) {
              e.preventDefault();
              e.stopPropagation();
              onSubmit();
            }
          }
        }}
        disabled={isBusy || disabled}
        autoSize={{ minRows: rows, maxRows: rows }}
      />
      {handleInputComponent}
    </div>
  );
};
